import { getContentGroups } from '@/api';
import { Device } from '@/classes';
import { ContentGroupCard } from '@/entities/content-group';
import { useExtendFocusable } from '@/hooks/useExtendFocusable';
import { GoToTopButton } from '@/shared/ui';
import { ContentGroup } from '@/types';
import { isFocusScrollSuppressed, scrollElementTo } from '@/utils';
import { FocusContext } from '@noriginmedia/norigin-spatial-navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type ContentGroupListProps = {
  containerClassName?: string;
  headerClassName?: string;
  header?: React.ReactNode;
};

// 최상단 IP 줄(아기상어·핑크퐁&호기·베베핀)은 거의 바뀌지 않아 3개만 잡아둔다.
const IP_SKELETON_COUNT = 3;
const TOPIC_SKELETON_COUNT = 10;

export const ContentGroupList = ({ containerClassName, headerClassName, header }: ContentGroupListProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [contentGroups, setContentGroups] = useState<Array<ContentGroup>>([]);

  // ! focusable: false — 컨테이너 자신은 절대 포커스 대상이 되지 않게 한다. (isContainer 주석 참고)
  const { ref, focusKey } = useExtendFocusable({
    isContainer: true,
    saveLastFocusedChild: true,
    trackChildren: true,
    autoRestoreFocus: true,
    isFocusBoundary: false,
    focusable: false,
  });

  const [loading, setLoading] = useState(false);
  const loadContentGroups = useCallback(async () => {
    try {
      setLoading(true);
      // ! resolution 을 안 넘기면 원본(500×500 PNG, ~150KB)이 내려온다. P60(300×300 WebP)이 280px 원형에 맞는다.
      const contentGroups = await getContentGroups({
        language: Device.language,
        thumbnailType: 'IMAGE',
        resolution: 'P60',
      });

      setContentGroups(contentGroups);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load content groups', e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const onFocus = useCallback(
    ({ top }: { top: number }) => {
      if (isFocusScrollSuppressed('vertical')) {
        return;
      }

      const current = scrollRef.current;
      if (current) {
        scrollElementTo({ element: current, top: top - 204 });
      }
    },
    [scrollRef],
  );

  const ipComponents = useMemo(() => {
    return contentGroups
      .filter((contentGroup) => contentGroup.type === 'IP')
      .map((contentGroup, index) => (
        <ContentGroupCard
          key={contentGroup.id}
          focusKey={`content-group-${index}`}
          contentGroup={contentGroup}
          onFocus={onFocus}
          weight={index}
        />
      ));
  }, [contentGroups, onFocus]);

  const topicComponents = useMemo(() => {
    const ipCount = contentGroups.filter((contentGroup) => contentGroup.type === 'IP').length;

    return contentGroups
      .filter((contentGroup) => contentGroup.type === 'TOPIC')
      .map((contentGroup, index) => (
        <ContentGroupCard
          key={contentGroup.id}
          focusKey={`content-group-${ipCount + index}`}
          contentGroup={contentGroup}
          onFocus={onFocus}
          weight={ipCount + index}
        />
      ));
  }, [contentGroups, onFocus]);

  const skeletonComponent = useMemo(() => {
    return (
      <div className="w-[1680px] mx-[53px] flex flex-row flex-wrap justify-center">
        {new Array(IP_SKELETON_COUNT).fill(0).map((_, i) => (
          <div key={i} className="w-[312px] h-[426px] mr-[24px] mb-[4px] flex flex-col items-center justify-center">
            <div className="bg-gray-400 animate-pulse rounded-full w-[280px] h-[280px] mx-[16px] mt-[20px]" />
            <div className="flex w-[292px] h-[80px] mt-[20px] mb-[26px] mx-[10px] font-bold text-center text-[34px] leading-[39.6px]" />
          </div>
        ))}

        <div className="w-full h-[1px] bg-gray-200 my-[16px]" />

        {new Array(TOPIC_SKELETON_COUNT).fill(0).map((_, i) => (
          <div key={i} className="w-[312px] h-[426px] mr-[24px] mb-[4px] flex flex-col items-center justify-center">
            <div className="bg-gray-400 animate-pulse rounded-full w-[280px] h-[280px] mx-[16px] mt-[20px]" />
            <div className="flex w-[292px] h-[80px] mt-[20px] mb-[26px] mx-[10px] font-bold text-center text-[34px] leading-[39.6px]" />
          </div>
        ))}
      </div>
    );
  }, []);

  useEffect(() => {
    loadContentGroups();
  }, [loadContentGroups]);

  return (
    <main id="main-container" className={containerClassName} ref={scrollRef}>
      {/* Header */}
      <div className={headerClassName}>{header}</div>

      <div className="pt-[134px]">
        {/* Skeleton */}
        {ipComponents.length === 0 && topicComponents.length === 0 && loading && skeletonComponent}

        {/* Content Groups */}
        {(ipComponents.length > 0 || topicComponents.length > 0) && (
          <FocusContext.Provider value={focusKey}>
            <div ref={ref} className="w-[1680px] mx-[53px] flex flex-row flex-wrap justify-center">
              {ipComponents}

              {ipComponents.length > 0 && topicComponents.length > 0 && (
                <div className="w-full h-[1px] bg-gray-200 my-[16px]" />
              )}

              {topicComponents}
            </div>
            <GoToTopButton focusKey="content-group-0" scrollingRef={scrollRef} />
          </FocusContext.Provider>
        )}
      </div>
    </main>
  );
};
